import { useParams, useLocation } from 'wouter';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GlobalHeader } from '@/components/GlobalHeader';
import { Sidebar, MobileSidebar } from '@/components/Sidebar';
import { useSupplementData } from '@/hooks/use-supplements';
import { useComparisons } from '@/hooks/use-comparisons';
import { useClaim } from '@/hooks/use-claim';
import { SupplementTabs } from '@/components/supplements';
import { useSupplementGenerationSSE } from '@/hooks/useSSEProgress';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

export default function SupplementDetail() {
  const params = useParams<{ id: string; claimId?: string }>();
  const [, setLocation] = useLocation();
  const supplementId = parseInt(params.id || '0');
  const queryClient = useQueryClient();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  
  const {
    supplement,
    rooms,
    totals,
    isLoading,
    isPending,
  } = useSupplementData(supplementId);

  // Fetch claim data to get the claim number
  const { claim } = useClaim(supplement?.claimId ?? 0);
  const canWrite = claim?.canWrite ?? false;

  // Fetch comparison data to get the comparison number
  const { comparisons: comparisons } = useComparisons(supplement?.claimId ?? 0);

  // Connect to SSE for real-time workflow progress
  // Only enable if supplement is in processing state
  const isProcessing = supplement?.workflowStatus === 'processing';
  const { isComplete, isFailed, progress } = useSupplementGenerationSSE(
    supplementId,
    isProcessing
  );

  // When workflow completes or fails, invalidate query to refetch supplement data
  useEffect(() => {
    if (isComplete || isFailed) {
      console.log('[SupplementDetail] Workflow finished, refetching supplement data');
      queryClient.invalidateQueries({ queryKey: ['supplement', supplementId] });
    }
  }, [isComplete, isFailed, supplementId, queryClient]);

  if (isLoading || isPending) {
    return (
      <div className="flex h-[100dvh] md:h-screen bg-slate-50 dark:bg-slate-950 overflow-hidden">
        <Sidebar />
        <MobileSidebar open={mobileNavOpen} onOpenChange={setMobileNavOpen} />
        <div className="flex-1 flex flex-col">
          <GlobalHeader onOpenMobileNav={() => setMobileNavOpen(true)} />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading supplement details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!supplement) {
    return (
      <div className="flex h-[100dvh] md:h-screen bg-slate-50 dark:bg-slate-950 overflow-hidden">
        <Sidebar />
        <MobileSidebar open={mobileNavOpen} onOpenChange={setMobileNavOpen} />
        <div className="flex-1 flex flex-col">
          <GlobalHeader onOpenMobileNav={() => setMobileNavOpen(true)} />
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <p className="text-lg text-destructive">Failed to load supplement details</p>
            <Button variant="outline" onClick={() => setLocation('/')}>
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] md:h-screen bg-slate-50 dark:bg-slate-950 overflow-hidden">
      <Sidebar />
      <MobileSidebar open={mobileNavOpen} onOpenChange={setMobileNavOpen} />

      <div className="flex-1 flex flex-col min-w-0 bg-slate-50 dark:bg-slate-950 overflow-hidden relative">
        <GlobalHeader onOpenMobileNav={() => setMobileNavOpen(true)} />

        {/* Processing Banner */}
        {isProcessing && (
          <div className="bg-amber-100 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800 px-4 sm:px-6 py-2 flex items-center justify-center gap-2 text-sm text-amber-800 dark:text-amber-200 animate-in slide-in-from-top-0 duration-300">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="font-medium text-center">
              {progress?.message || 'Generating supplement justifications... The page will update automatically.'}
            </span>
          </div>
        )}

        {claim && !canWrite && (
          <div className="bg-amber-100 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800 px-4 sm:px-6 py-2 flex items-center justify-center text-sm text-amber-800 dark:text-amber-200">
            <span className="font-medium text-center">View-only access: supplement editing actions are blocked.</span>
          </div>
        )}

        {/* Content Area */}
        <div className={cn(
          "flex-1 overflow-auto transition-opacity duration-200",
          isProcessing ? "opacity-70 pointer-events-none grayscale-[0.3]" : ""
        )}>
          <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
            <SupplementTabs
              supplement={supplement}
              rooms={rooms}
              totals={totals}
              isLoading={isLoading || isPending}
              comparisonName={comparisons[0]?.name}
              claimNumber={claim?.claimNumber}
              canWrite={canWrite}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
